import { MuscleGroup, MuscleGroupOverrides } from '../types'
import { exerciseTemplates, findExerciseTemplateByName } from '../utils/exercise-dict'

export interface MuscleContribution {
  muscleGroup: MuscleGroup
  weight: number
  isPrimary: boolean
}

export interface RawExerciseData {
  templateId: string
  name: string
}

export interface ProcessedExercise extends RawExerciseData {
  primaryMover: MuscleGroup
  secondaryMovers: MuscleGroup[]
  isBodyweight: boolean
  isAssisted: boolean
  detectionMethod: 'override' | 'template_id' | 'pattern_matching' | 'unknown'
}

export interface ExerciseMatchResult {
  primaryMover: MuscleGroup
  secondaryMovers: MuscleGroup[]
  isBodyweight: boolean
  isAssisted: boolean
  confidence: 'override' | 'high' | 'medium' | 'low'
}

/**
 * Central service for processing exercise data and determining muscle groups
 * Priority: 1. User overrides, 2. Template ID lookup, 3. Pattern matching fallback, 4. Unknown default
 */
export class ExerciseProcessor {
  private static instance: ExerciseProcessor
  private patternCache = new Map<string, ExerciseMatchResult>()
  private muscleGroupOverrides: MuscleGroupOverrides = {}

  static getInstance(): ExerciseProcessor {
    if (!ExerciseProcessor.instance) {
      ExerciseProcessor.instance = new ExerciseProcessor()
    }
    return ExerciseProcessor.instance
  }

  /**
   * Set user muscle group overrides
   */
  setMuscleGroupOverrides(overrides: MuscleGroupOverrides): void {
    this.muscleGroupOverrides = overrides
  }

  /**
   * Main processing function - tries overrides first, then template ID, then name lookup, then pattern matching
   */
  processExercise(exercise: RawExerciseData): ProcessedExercise {
    // Try user override first (highest priority)
    const overrideResult = this.tryOverrideLookup(exercise.templateId)
    if (overrideResult) {
      return {
        ...exercise,
        ...overrideResult,
        detectionMethod: 'override'
      }
    }

    // Try template ID lookup second (for API data)
    const templateResult = this.tryTemplateIdLookup(exercise.templateId)
    if (templateResult) {
      return {
        ...exercise,
        ...templateResult,
        detectionMethod: 'template_id'
      }
    }

    // Try name-based template lookup (for CSV imports)
    const nameResult = this.tryNameBasedLookup(exercise.name)
    if (nameResult) {
      return {
        ...exercise,
        ...nameResult,
        detectionMethod: 'template_id' // Still counts as template detection
      }
    }

    // Fallback to pattern matching
    const patternResult = this.tryPatternMatching(exercise.name)
    if (patternResult) {
      return {
        ...exercise,
        ...patternResult,
        detectionMethod: 'pattern_matching'
      }
    }

    // Last resort - unknown exercise
    console.warn(`Unknown exercise: ${exercise.name} (ID: ${exercise.templateId})`)
    return {
      ...exercise,
      primaryMover: 'chest', // Default fallback
      secondaryMovers: [],
      isBodyweight: false,
      isAssisted: false,
      detectionMethod: 'unknown'
    }
  }

  /**
   * Batch process multiple exercises efficiently
   */
  processExercises(exercises: RawExerciseData[]): ProcessedExercise[] {
    return exercises.map((exercise) => this.processExercise(exercise))
  }

  /**
   * User override lookup - highest priority
   */
  private tryOverrideLookup(templateId: string): ExerciseMatchResult | null {
    const override = this.muscleGroupOverrides[templateId]
    if (!override) {
      return null
    }

    return {
      primaryMover: override.primary_mover,
      secondaryMovers: override.secondary_movers,
      isBodyweight: false, // User overrides don't affect bodyweight status
      isAssisted: false, // User overrides don't affect assisted status
      confidence: 'override'
    }
  }

  /**
   * Template ID lookup - high accuracy
   * Now also supports name-based lookup for CSV imports
   */
  private tryTemplateIdLookup(templateId: string): ExerciseMatchResult | null {
    // First try direct template ID lookup (for API data)
    const template = exerciseTemplates[templateId]
    if (template) {
      return {
        primaryMover: template.primary_movers as MuscleGroup,
        secondaryMovers: template.secondary_movers as MuscleGroup[],
        isBodyweight: template.bodyweight,
        isAssisted: template.assisted,
        confidence: 'high'
      }
    }

    return null
  }

  /**
   * Name-based template lookup - for CSV imports
   */
  private tryNameBasedLookup(exerciseName: string): ExerciseMatchResult | null {
    const template = findExerciseTemplateByName(exerciseName)
    if (!template) {
      return null
    }

    return {
      primaryMover: template.primary_movers as MuscleGroup,
      secondaryMovers: template.secondary_movers as MuscleGroup[],
      isBodyweight: template.bodyweight,
      isAssisted: template.assisted,
      confidence: 'high'
    }
  }

  /**
   * Pattern matching fallback - existing logic for unknown exercises
   */
  private tryPatternMatching(exerciseName: string): ExerciseMatchResult | null {
    // Check cache first
    const cached = this.patternCache.get(exerciseName.toLowerCase())
    if (cached) {
      return cached
    }

    const result = this.performPatternMatching(exerciseName)
    if (result) {
      this.patternCache.set(exerciseName.toLowerCase(), result)
    }

    return result
  }

  /**
   * Multilingual pattern matching logic with support for all Hevy languages
   */
  private performPatternMatching(exerciseName: string): ExerciseMatchResult | null {
    const name = exerciseName.toLowerCase()

    // Chest exercises (English, Spanish, German, French, Italian, Portuguese, Turkish, Russian)
    if (
      this.matchesAnyPattern(name, [
        // English
        'bench',
        'chest',
        'fly',
        'press',
        'pec',
        'push up',
        'pushup',
        // Spanish
        'banco',
        'pecho',
        'vuelo',
        'press',
        'pectoral',
        'flexión',
        'flexion',
        // German
        'bank',
        'brust',
        'fliegen',
        'drücken',
        'liegestütz',
        'liegestutze',
        // French
        'banc',
        'poitrine',
        'écarté',
        'ecarte',
        'développé',
        'developpe',
        'pompe',
        // Italian
        'panca',
        'petto',
        'croci',
        'spinta',
        'flessione',
        // Portuguese
        'supino',
        'peito',
        'voador',
        'flexão',
        'flexao',
        // Turkish
        'göğüs',
        'gogus',
        'bench',
        'sinav',
        'şınav',
        // Russian
        'жим',
        'грудь',
        'разведение',
        'отжимание',
        'отжимания'
      ])
    ) {
      return {
        primaryMover: 'chest',
        secondaryMovers: ['frontDelts', 'triceps'],
        isBodyweight: this.matchesAnyPattern(name, [
          'push up',
          'pushup',
          'flexión',
          'flexion',
          'liegestütz',
          'liegestutze',
          'pompe',
          'flessione',
          'flexão',
          'flexao',
          'sinav',
          'şınav',
          'отжимание',
          'отжимания'
        ]),
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Deadlift exercises (check before pull exercises to avoid Russian "тяга" conflict)
    if (
      this.matchesAnyPattern(name, [
        // English
        'deadlift',
        // Spanish
        'peso muerto',
        'levantamiento',
        // German
        'kreuzheben',
        'deadlift',
        // French
        'soulevé de terre',
        'souleve',
        // Italian
        'stacco',
        'deadlift',
        // Portuguese
        'levantamento terra',
        'deadlift',
        // Turkish
        'deadlift',
        'ölü kaldırma',
        'olu kaldirma',
        // Russian
        'становая тяга',
        'мертвая тяга',
        'становая'
      ])
    ) {
      return {
        primaryMover: 'glutes',
        secondaryMovers: ['hamstrings', 'lats', 'upperBack'],
        isBodyweight: false,
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Pull exercises (Pull-ups, Lat pulldowns, Rows)
    if (
      this.matchesAnyPattern(name, [
        // English
        'pull',
        'lat',
        'row',
        'chin up',
        'pullup',
        // Spanish
        'dominada',
        'jalón',
        'jalon',
        'remo',
        'tirón',
        'tiron',
        // German
        'klimmzug',
        'latzug',
        'rudern',
        'ziehen',
        // French
        'traction',
        'tirage',
        'rameur',
        'rowing',
        // Italian
        'trazioni',
        'lat machine',
        'rematore',
        'dorsali',
        // Portuguese
        'barra fixa',
        'pulldown',
        'remada',
        'puxada',
        // Turkish
        'barfiks',
        'çekiş',
        'cekis',
        'lat',
        // Russian
        'подтягивание',
        'тяга',
        'гребля'
      ])
    ) {
      return {
        primaryMover: 'lats',
        secondaryMovers: ['upperBack', 'biceps'],
        isBodyweight: this.matchesAnyPattern(name, [
          'pull up',
          'pullup',
          'chin up',
          'dominada',
          'klimmzug',
          'traction',
          'trazioni',
          'barra fixa',
          'barfiks',
          'подтягивание'
        ]),
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Squat exercises
    if (
      this.matchesAnyPattern(name, [
        // English
        'squat',
        // Spanish
        'sentadilla',
        'cuclilla',
        // German
        'kniebeuge',
        'hocke',
        // French
        'squat',
        'accroupissement',
        // Italian
        'squat',
        'accosciata',
        // Portuguese
        'agachamento',
        'squat',
        // Turkish
        'squat',
        'çömelme',
        'comelme',
        // Russian
        'приседание',
        'приседания'
      ])
    ) {
      return {
        primaryMover: 'quads',
        secondaryMovers: ['glutes'],
        isBodyweight: !this.matchesAnyPattern(name, [
          'barbell',
          'dumbbell',
          'machine',
          'barra',
          'mancuerna',
          'langhantel',
          'kurzhantel',
          'maschine',
          'barre',
          'haltère',
          'bilanciere',
          'manubrio',
          'macchina',
          'halter',
          'máquina',
          'makine',
          'штанга',
          'гантель',
          'тренажер'
        ]),
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Bicep exercises
    if (
      this.matchesAnyPattern(name, [
        // English
        'curl',
        'bicep',
        'arm',
        // Spanish
        'curl',
        'bíceps',
        'biceps',
        'brazo',
        // German
        'curl',
        'bizeps',
        'arm',
        // French
        'curl',
        'biceps',
        'bras',
        // Italian
        'curl',
        'bicipiti',
        'braccio',
        // Portuguese
        'rosca',
        'bíceps',
        'biceps',
        'braço',
        'braco',
        // Turkish
        'curl',
        'biceps',
        'kol',
        // Russian
        'сгибание',
        'бицепс',
        'рука'
      ]) &&
      (name.includes('curl') ||
        name.includes('rosca') ||
        name.includes('сгибание') ||
        name.includes('bicep') ||
        name.includes('bíceps') ||
        name.includes('biceps') ||
        name.includes('bizeps') ||
        name.includes('bicipiti'))
    ) {
      return {
        primaryMover: 'biceps',
        secondaryMovers: ['forearms'],
        isBodyweight: false,
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Tricep exercises
    if (
      this.matchesAnyPattern(name, [
        // English
        'tricep',
        'extension',
        'pushdown',
        'dip',
        // Spanish
        'tríceps',
        'triceps',
        'extensión',
        'extension',
        'fondo',
        // German
        'trizeps',
        'strecken',
        'dip',
        // French
        'triceps',
        'extension',
        'dips',
        // Italian
        'tricipiti',
        'estensione',
        'dip',
        // Portuguese
        'tríceps',
        'triceps',
        'extensão',
        'extensao',
        'mergulho',
        // Turkish
        'triceps',
        'uzatma',
        'dip',
        // Russian
        'трицепс',
        'разгибание',
        'отжимание'
      ])
    ) {
      return {
        primaryMover: 'triceps',
        secondaryMovers: [],
        isBodyweight: this.matchesAnyPattern(name, [
          'dip',
          'fondo',
          'dips',
          'mergulho',
          'отжимание'
        ]),
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Shoulder exercises
    if (
      this.matchesAnyPattern(name, [
        // English
        'shoulder',
        'lateral',
        'raise',
        'deltoid',
        // Spanish
        'hombro',
        'lateral',
        'elevación',
        'elevacion',
        'deltoide',
        // German
        'schulter',
        'lateral',
        'heben',
        'deltoid',
        // French
        'épaule',
        'epaule',
        'latéral',
        'lateral',
        'élévation',
        'elevation',
        'deltoïde',
        // Italian
        'spalla',
        'laterale',
        'alzata',
        'deltoide',
        // Portuguese
        'ombro',
        'lateral',
        'elevação',
        'elevacao',
        'deltoide',
        // Turkish
        'omuz',
        'lateral',
        'kaldırma',
        'kaldirma',
        'deltoid',
        // Russian
        'плечо',
        'дельта',
        'подъем',
        'подъём'
      ])
    ) {
      return {
        primaryMover: 'lateralDelts',
        secondaryMovers: ['frontDelts'],
        isBodyweight: false,
        isAssisted: this.matchesAnyPattern(name, [
          'assisted',
          'asistido',
          'unterstützt',
          'assisté',
          'assistito',
          'assistido',
          'yardımlı',
          'с помощью'
        ]),
        confidence: 'medium'
      }
    }

    // Leg extension exercises
    if (
      this.matchesAnyPattern(name, [
        'leg',
        'pierna',
        'bein',
        'jambe',
        'gamba',
        'perna',
        'bacak',
        'нога'
      ]) &&
      this.matchesAnyPattern(name, [
        'extension',
        'extensión',
        'extension',
        'estensione',
        'extensão',
        'extensao',
        'uzatma',
        'разгибание'
      ])
    ) {
      return {
        primaryMover: 'quads',
        secondaryMovers: [],
        isBodyweight: false,
        isAssisted: false,
        confidence: 'medium'
      }
    }

    // Leg curl exercises
    if (
      this.matchesAnyPattern(name, [
        'leg',
        'pierna',
        'bein',
        'jambe',
        'gamba',
        'perna',
        'bacak',
        'нога'
      ]) &&
      this.matchesAnyPattern(name, [
        'curl',
        'flexión',
        'flexion',
        'beugung',
        'flexion',
        'flessione',
        'flexão',
        'flexao',
        'kıvrım',
        'сгибание'
      ])
    ) {
      return {
        primaryMover: 'hamstrings',
        secondaryMovers: [],
        isBodyweight: false,
        isAssisted: false,
        confidence: 'medium'
      }
    }

    // Calf exercises
    if (
      this.matchesAnyPattern(name, [
        // English
        'calf',
        // Spanish
        'pantorrilla',
        'gemelo',
        // German
        'wade',
        'kalb',
        // French
        'mollet',
        // Italian
        'polpaccio',
        // Portuguese
        'panturrilha',
        'batata da perna',
        // Turkish
        'baldır',
        'baldir',
        // Russian
        'икра',
        'икры'
      ])
    ) {
      return {
        primaryMover: 'calves',
        secondaryMovers: [],
        isBodyweight: !this.matchesAnyPattern(name, [
          'machine',
          'barbell',
          'máquina',
          'maquina',
          'barra',
          'maschine',
          'langhantel',
          'machine',
          'barre',
          'macchina',
          'bilanciere',
          'máquina',
          'halter',
          'makine',
          'тренажер',
          'штанга'
        ]),
        isAssisted: false,
        confidence: 'medium'
      }
    }

    // Ab exercises
    if (
      this.matchesAnyPattern(name, [
        // English
        'crunch',
        'plank',
        'abs',
        'abdominal',
        // Spanish
        'crunch',
        'plancha',
        'abdominales',
        'abdominal',
        // German
        'crunch',
        'plank',
        'bauch',
        'bauchmuskeln',
        // French
        'crunch',
        'planche',
        'abdos',
        'abdominaux',
        // Italian
        'crunch',
        'plank',
        'addominali',
        'abs',
        // Portuguese
        'abdominal',
        'prancha',
        'abdômen',
        'abdomen',
        // Turkish
        'mekik',
        'plank',
        'karın',
        'karin',
        // Russian
        'пресс',
        'планка',
        'живот',
        'скручивание'
      ])
    ) {
      return {
        primaryMover: 'abs',
        secondaryMovers: [],
        isBodyweight: !this.matchesAnyPattern(name, [
          'machine',
          'weighted',
          'máquina',
          'maquina',
          'con peso',
          'maschine',
          'gewichtet',
          'machine',
          'lesté',
          'macchina',
          'pesato',
          'máquina',
          'com peso',
          'makine',
          'ağırlıklı',
          'тренажер',
          'с весом'
        ]),
        isAssisted: false,
        confidence: 'medium'
      }
    }

    return null
  }

  /**
   * Helper method to check if a name matches any of the given patterns
   */
  private matchesAnyPattern(name: string, patterns: string[]): boolean {
    return patterns.some((pattern) => name.includes(pattern))
  }

  /**
   * Get template coverage statistics for monitoring
   */
  getTemplateCoverage(exercises: RawExerciseData[]): {
    total: number
    overrideMatches: number
    templateMatches: number
    patternMatches: number
    unknown: number
    coverage: number
  } {
    const processed = this.processExercises(exercises)
    const overrideMatches = processed.filter((e) => e.detectionMethod === 'override').length
    const templateMatches = processed.filter((e) => e.detectionMethod === 'template_id').length
    const patternMatches = processed.filter((e) => e.detectionMethod === 'pattern_matching').length
    const unknown = processed.filter((e) => e.detectionMethod === 'unknown').length

    return {
      total: exercises.length,
      overrideMatches,
      templateMatches,
      patternMatches,
      unknown,
      coverage: (overrideMatches + templateMatches) / exercises.length
    }
  }

  /**
   * Get muscle contributions for a processed exercise
   * Get muscle contributions for a processed exercise with weights
   */
  getMuscleContributions(
    processedExercise: ProcessedExercise,
    includeSecondaryMovers = false
  ): MuscleContribution[] {
    const contributions: MuscleContribution[] = []

    // Always include primary mover
    contributions.push({
      muscleGroup: processedExercise.primaryMover,
      weight: 1.0,
      isPrimary: true
    })

    // Add secondary movers if requested
    if (includeSecondaryMovers && processedExercise.secondaryMovers.length > 0) {
      processedExercise.secondaryMovers.forEach((muscleGroup) => {
        contributions.push({
          muscleGroup,
          weight: 0.5,
          isPrimary: false
        })
      })
    }

    return contributions
  }

  /**
   * Convenience method to get muscle contributions directly from exercise name/template
   */
  getMuscleContributionsFromExercise(
    exerciseName: string,
    templateId?: string,
    includeSecondaryMovers = false
  ): MuscleContribution[] {
    const processedExercise = this.processExercise({
      templateId: templateId || `manual_${exerciseName.replace(/\s+/g, '_').toLowerCase()}`,
      name: exerciseName
    })

    return this.getMuscleContributions(processedExercise, includeSecondaryMovers)
  }

  /**
   * Group exercises by their primary muscle group
   * Group exercises by their primary muscle group
   */
  groupExercisesByMuscleGroup<T extends { name: string; templateId?: string }>(
    exercises: T[]
  ): Record<string, T[]> {
    const grouped: Record<string, T[]> = {}

    exercises.forEach((exercise) => {
      const processedExercise = this.processExercise({
        templateId:
          exercise.templateId || `manual_${exercise.name.replace(/\s+/g, '_').toLowerCase()}`,
        name: exercise.name
      })

      const primaryGroup = processedExercise.primaryMover

      if (!grouped[primaryGroup]) {
        grouped[primaryGroup] = []
      }
      grouped[primaryGroup].push(exercise)
    })

    return grouped
  }

  /**
   * Clear pattern matching cache (for testing or memory management)
   */
  clearCache(): void {
    this.patternCache.clear()
  }
}

// Export singleton instance
export const exerciseProcessor = ExerciseProcessor.getInstance()
